import { useState } from 'react';
import { Icono } from '@xiltrion/ui/icono';
import { Button } from '@xiltrion/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@xiltrion/ui/popover';
import { Skeleton } from '@xiltrion/ui/skeleton';
import { cn } from '@xiltrion/ui/utils';
import { fmtDate } from '@/components/ui';
import { useAssignmentExplanation } from '../api';
import { REASSIGN_REASON_LABEL } from '../lib';

const KIND_LABEL: Record<string, string> = { AUTO: 'Asignación automática', MANUAL: 'Asignación manual', REASSIGN: 'Reasignación', CLAIM: 'Tomada por el usuario' };
/** Etiquetas por código, para que los registros viejos también se lean igual. */
const CHECK_LABEL: Record<string, string> = { AVAILABLE: 'Disponible', CAPACITY: 'Con capacidad' };
const checkLabel = (c: Check) => c.code === 'SPECIALTY' && c.informative ? (c.passed ? 'Tiene la especialidad' : 'Sin la especialidad') : CHECK_LABEL[c.code] ?? c.label;

export interface Check { code: string; label: string; passed: boolean; detail?: string; informative?: boolean }
/**
 * La lista de verificaciones de un «¿Por qué?». Cumple: chulito verde; no cumple: equis roja. Una verificación
 * `informative` que no se cumple (no tiene la especialidad que solo suma puntaje) no excluye: va en gris.
 */
export function CheckList({ checks }: { checks: Check[] }) {
  return (
    <ul className="list-none m-0 p-0 flex flex-col gap-0.5">
      {checks.map((c) => {
        const muted = c.informative && !c.passed;
        return (
          <li key={c.code} className={cn('flex items-start gap-2', muted ? 'text-muted-foreground' : c.passed ? 'text-success' : 'text-destructive')}>
            <Icono nombre={muted ? 'mdi:information-outline' : c.passed ? 'mdi:check-circle' : 'mdi:close-circle'} className="mt-0.5 shrink-0" aria-label={muted ? 'No suma puntaje' : c.passed ? 'Cumple' : 'No cumple'} />
            <span>{c.label}{c.detail ? <span className="text-muted-foreground"> — {c.detail}</span> : null}</span>
          </li>
        );
      })}
    </ul>
  );
}

/** “¿Por qué fue asignado?” popover with the ✓ checks list. */
export function AssignmentExplanation({ caseId }: { caseId: string }) {
  const [open, setOpen] = useState(false);
  const q = useAssignmentExplanation(caseId, open);
  const e = q.data;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild><Button variant="link" size="xs" aria-haspopup="dialog"><Icono nombre='mdi:help-circle-outline' /> ¿Por qué?</Button></PopoverTrigger>
      <PopoverContent className="w-[min(380px,95vw)]" align="start">
        {q.isLoading ? <Skeleton className="h-24" /> : q.isError ? <div className="text-destructive text-sm">No pudimos cargar la explicación.</div> : !e ? <div className="text-muted-foreground text-sm">No hay un registro de asignación para este expediente.</div> : (
          <div className="text-sm flex flex-col gap-2">
            <div><strong>{KIND_LABEL[e.kind] ?? e.kind}</strong> a {e.userName ?? 'usuario'} · {fmtDate(e.createdAt)}</div>
            {e.reasonCode && <div>Motivo: {REASSIGN_REASON_LABEL[e.reasonCode] ?? e.reasonCode}{e.comment ? ` · “${e.comment}”` : ''}</div>}
            {e.checks?.length ? <CheckList checks={e.checks.map((c) => ({ code: c.code, label: checkLabel(c), passed: c.passed, informative: c.informative }))} /> : e.kind === 'AUTO' ? <div className="text-muted-foreground">El motor no guardó verificaciones.</div> : null}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
